import React, { useState, ReactNode } from 'react';
import { useAppDispatch, useAppSelector, useAppStore } from '@/lib/hooks';
import { setModalName } from '@/lib/slices/modal-slice';
import ModalComponent from '@/common/components/modals/ModalComponent';
import Button from '@/common/components/ui/Button';
import { CancelIcon } from '@/assets/icons/button';
import LoadingSpinner from '../../LoadingSpinner';

interface FormModalProps {
  modalName: string;
  title: string;
  subtitle?: string;
  children: ReactNode;
  onSubmit: () => Promise<void>;
  submitButton?: {
    text?: string;
    icon?: ReactNode;
    disabled?: boolean;
  };
  advancedOptions?: {
    content: ReactNode;
    toggleText?: string;
    sectionClassName?: string;
    externalState?: [boolean, React.Dispatch<React.SetStateAction<boolean>>];
  };
  className?: string;
  closeOnClickOff?: boolean;
  loadingText?: string;
}

export default function FormModal({
  modalName,
  title,
  subtitle,
  children,
  onSubmit,
  submitButton,
  advancedOptions,
  className,
  closeOnClickOff = true,
  loadingText = 'Loading...',
}: FormModalProps) {
  const store = useAppStore();
  const dispatch = useAppDispatch();

  const modalVisible: boolean = useAppSelector(() => store.getState().modal.visible);
  const currentModalName: string = useAppSelector(() => store.getState().modal.name);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [internalShowAdvanced, setInternalShowAdvanced] = useState<boolean>(false);

  const showAdvanced = advancedOptions?.externalState
    ? advancedOptions.externalState[0]
    : internalShowAdvanced;
  const setShowAdvanced = advancedOptions?.externalState
    ? advancedOptions.externalState[1]
    : setInternalShowAdvanced;

  const handleClose = () => {
    if (isLoading) return;
    dispatch(setModalName(''));
    setShowAdvanced(false);
  };

  const handleSubmit = async (e?: React.MouseEvent) => {
    e?.preventDefault();
    if (submitButton?.disabled || isLoading) return;

    setIsLoading(true);
    try {
      await onSubmit();
      handleClose();
    } catch (error) {
      // Check if this is a special "skip close" error
      if (error instanceof Error && error.message === 'SKIP_CLOSE') {
        // Don't close modal, just stop loading
        setIsLoading(false);
        return;
      }
      console.error('Form submission failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const isOpen = modalVisible && currentModalName === modalName;

  return (
    <ModalComponent
      isOpen={isOpen}
      onRequestClose={handleClose}
      contentLabel={title}
      contentSubtitle={subtitle}
      className={className}
      closeOnClickOff={closeOnClickOff}
      footer={
        <div className='w-full pt-4 flex flex-col gap-2'>
          <div className='flex flex-row gap-8 py-2'>
            <Button onClick={handleSubmit} disabled={isLoading || submitButton?.disabled}>
              {submitButton?.icon}
              {submitButton?.text || 'Submit'}
            </Button>
            <Button outline={true} onClick={handleClose} disabled={isLoading}>
              <CancelIcon className='w-[20px] h-[20px] mr-2' /> Cancel
            </Button>
          </div>
        </div>
      }
    >
      <div className='relative w-full'>
        <div className='flex flex-col gap-[16px] w-full'>{children}</div>
        {advancedOptions && (
          <div className='w-full pt-4 flex flex-col gap-2'>
            <div
              className='px-[14px] w-fit py-2 text-primary font-semibold text-sm leading-5 tracking-[0%] cursor-pointer'
              onClick={() => setShowAdvanced((prev: boolean) => !prev)}
            >
              {advancedOptions.toggleText || 'Advanced Options'}
            </div>
            {showAdvanced && advancedOptions.content && (
              <div className={advancedOptions.sectionClassName}>{advancedOptions.content}</div>
            )}
          </div>
        )}

        {isLoading && (
          <div className='absolute top-0 left-0 z-10 w-full h-full flex items-center justify-center'>
            <div className='absolute inset-0 bg-surface_gray_50 opacity-70' />
            <div className='flex flex-col items-center justify-center gap-2 text-sm z-20'>
              <LoadingSpinner />
              <div>{loadingText}</div>
            </div>
          </div>
        )}
      </div>
    </ModalComponent>
  );
}
